/**
 * In-memory normalized entity store, backed by TanStack DB collections.
 *
 * The app's reactive read model: one TanStack collection per entity type, written through the
 * sync-transaction protocol (server-sourced rows are "synced" data, never optimistic mutations).
 * TanStack DB owns row storage, secondary indexes, and change streams; reads happen through live
 * queries (`createLiveQueryCollection`/`useLiveQuery`), collection change subscriptions, or raw
 * row access. This class keeps the app-facing write API (batched merge-upserts) plus the
 * write-notification coalescing the snapshot scheduler rides on. Writes are O(delta) and
 * synchronous; durability is handled separately by the snapshot layer, off the write path.
 */

import { BasicIndex, type Collection, createCollection } from "@tanstack/db";

import { createLogger } from "@/logger";
import { recordRead } from "@/store/readTracking";

const log = createLogger("db");

export type CollectionDef<T> = {
  key: (row: T) => string;
};

type Listener = () => void;

/** Row counts one flush carried for one collection; see {@link EntityStore.observeFlushes}. */
export type FlushCounts = { written: number; unchanged: number; deleted: number };

/** Receives each flush's per-collection row counts, before the write listeners run. */
export type FlushObserver = (counts: ReadonlyMap<string, FlushCounts>) => void;

/** The row keys one write op changed in one collection; see {@link EntityStore.subscribeRowChanges}. */
export type RowChange<Name> = {
  collection: Name;
  /** Keys inserted or replaced; the rows are readable in the store when the listener runs. */
  upserted: readonly string[];
  deleted: readonly string[];
  /** Every row was dropped. */
  truncated: boolean;
};

/**
 * Whether two rows hold the same value in every own field.
 *
 * Shallow on purpose: rows are flat records, and a nested value compares by reference, so a false "changed" costs
 * one redundant write rather than a missed one. The one miss it can make is a writer that mutates a nested value in
 * place and upserts a new outer object still holding it; the `existing === next` check this extends shares that blind
 * spot, and rows must be treated as immutable either way.
 */
function sameRow(a: object, b: object): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const key of aKeys) {
    if (!Object.is(a[key as keyof typeof a], b[key as keyof typeof b])) return false;
  }
  return true;
}

/**
 * What the next flush has to write for one collection, tracked by row key rather than by
 * collection. The collection-level dirty set this replaced meant one changed row cost a rewrite of
 * every row in the collection, so persist duration scaled with workspace size instead of with the
 * change.
 */
export type CollectionDirty = {
  /** Every row was dropped (a `clear`); the flush truncates on disk before applying `upserts`. */
  truncated: boolean;
  /** Row keys inserted or updated since the last flush. */
  upserts: Set<string>;
  /** Row keys deleted since the last flush. */
  deletes: Set<string>;
};

export type DirtyRows<Schema> = Map<keyof Schema, CollectionDirty>;

type SyncHandles<T extends object> = {
  begin: () => void;
  write: (message: { type: "insert" | "update"; value: T } | { type: "delete"; key: string }) => void;
  commit: () => void;
  truncate: () => void;
};

type StoreCollection<T extends object> = {
  collection: Collection<T, string>;
  handles: SyncHandles<T>;
};

/**
 * Read-only view of a collection's rows keyed by row key. Backed by the collection's synced-data
 * map; iteration order is key-sorted (TanStack keeps synced rows in a SortedMap).
 */
export type RowMap<T> = {
  readonly size: number;
  get(key: string): T | undefined;
  has(key: string): boolean;
  keys(): IterableIterator<string>;
  values(): IterableIterator<T>;
  entries(): IterableIterator<[string, T]>;
  [Symbol.iterator](): IterableIterator<[string, T]>;
};

function createEntityCollection<T extends object>(name: string, key: (row: T) => string): StoreCollection<T> {
  let captured: SyncHandles<T> | undefined;
  const collection = createCollection<T, string>({
    id: name,
    getKey: key,
    // The store is the app's data backbone: sync from creation and never GC, so the captured
    // write handles stay valid for the store's whole lifetime. Unlike TanStack Query, TanStack DB
    // treats gcTime <= 0 as "GC disabled" (the lifecycle manager skips its cleanup timer).
    gcTime: 0,
    startSync: true,
    // Live-query joins and where clauses create the indexes they need on demand.
    autoIndex: "eager",
    defaultIndexType: BasicIndex,
    sync: {
      // Rows are whole replacements (the pre-TanStack store and the SQL tables before it both
      // stored full rows), so updates must not shallow-merge into the previous row.
      rowUpdateMode: "full",
      sync: (params) => {
        captured = params;
        params.markReady();
      },
    },
  });
  if (!captured) {
    throw new Error(`Sync handles were not captured for collection: ${name}`);
  }
  return { collection, handles: captured };
}

export class EntityStore<Schema extends Record<string, object>> {
  private readonly entries = new Map<keyof Schema, StoreCollection<Schema[keyof Schema]>>();
  private readonly versions = new Map<keyof Schema, number>();
  private dirtySinceSnapshot: DirtyRows<Schema> = new Map();
  private readonly writeListeners = new Set<Listener>();
  private batchDepth = 0;
  private pendingWrites = false;
  private trackDirty = true;
  private longOpenBatchWarningMs: number | null = null;
  private longOpenBatchTimer: ReturnType<typeof setTimeout> | null = null;
  private flushObserver: FlushObserver | null = null;
  private flushCounts = new Map<string, FlushCounts>();
  private readonly rowChangeListeners = new Set<(change: RowChange<keyof Schema>) => void>();

  public constructor(private readonly defs: { [K in keyof Schema]: CollectionDef<Schema[K]> }) {
    for (const name of Object.keys(defs) as (keyof Schema)[]) {
      this.entries.set(name, createEntityCollection(name as string, defs[name].key));
    }
  }

  /** The underlying TanStack collection — the handle for live queries and index creation. */
  public collection<K extends keyof Schema>(name: K): Collection<Schema[K], string> {
    return this.entry(name).collection;
  }

  /**
   * Reads go through the state manager rather than the public `Collection.get`, which enriches
   * every row with virtual props (`$synced`, `$origin`, …) on a spread copy. Raw reads keep exact
   * row shapes and object identity — snapshot persistence, merge short-circuits, and row-level
   * subscriptions all depend on that.
   */
  public get<K extends keyof Schema>(name: K, key: string | null | undefined): Schema[K] | undefined {
    if (key == null) return undefined;
    recordRead(name);
    return this.entry(name).collection._state.get(key);
  }

  /** The live synced-row map — treat as read-only; iteration order is key-sorted. */
  public getAll<K extends keyof Schema>(name: K): RowMap<Schema[K]> {
    recordRead(name);
    return this.entry(name).collection._state.syncedData;
  }

  public size(name: keyof Schema): number {
    recordRead(name);
    return this.entry(name).collection.size;
  }

  /**
   * Monotonic combined version across collections, bumped synchronously by every write. Serves as
   * the `useSyncExternalStore` snapshot for compute subscriptions: change events may reach a
   * subscriber after it attached, but the version already reflects every committed write, so
   * React's post-subscribe snapshot check catches writes that landed between render and subscribe.
   */
  public versionOf(names: readonly (keyof Schema)[]): number {
    let sum = 0;
    for (const name of names) sum += this.versions.get(name) ?? 0;
    return sum;
  }

  /**
   * Inserts or updates rows in one sync transaction. `merge` reproduces SQL
   * `ON CONFLICT DO UPDATE SET <subset>` semantics: given the existing and incoming row it
   * returns what to store, letting callers preserve client-owned fields. A row whose result equals
   * the stored one field for field is left in place: no write, no version bump, same identity.
   * Returns the stored rows.
   */
  public upsert<K extends keyof Schema>(
    name: K,
    incoming: readonly Schema[K][],
    merge?: (existing: Schema[K], incoming: Schema[K]) => Schema[K],
  ): Schema[K][] {
    if (incoming.length === 0) return [];
    const { collection, handles } = this.entry(name);
    const keyFn = this.defs[name].key;
    const stored: Schema[K][] = [];
    // Sync writes only apply at commit, so rows written earlier in this batch are tracked here to
    // keep read-your-writes merge semantics for duplicate keys within one upsert.
    const written = new Map<string, Schema[K]>();
    let began = false;
    try {
      for (const row of incoming) {
        const key = keyFn(row);
        const existing = written.get(key) ?? collection._state.get(key);
        const next = existing !== undefined && merge ? merge(existing, row) : row;
        // A re-fetch of rows the store already holds (sync cursor overlap, a repeated AppSync signal) changes
        // nothing; keeping the existing row keeps its identity and leaves every compute watching it idle.
        if (existing !== undefined && (existing === next || sameRow(existing, next))) {
          this.countFlush(name, "unchanged", 1);
          stored.push(existing);
          continue;
        }
        if (!began) {
          handles.begin();
          began = true;
        }
        handles.write({ type: existing !== undefined ? "update" : "insert", value: next });
        written.set(key, next);
        stored.push(next);
      }
    } finally {
      // A throwing merge/key callback must not leave the sync transaction open — that would wedge
      // every later write to this collection. Committing keeps the partial batch applied and the
      // store usable, the same failure mode as the pre-TanStack Map-based store.
      if (began) {
        handles.commit();
        this.markUpserts(name, written.keys());
      }
      // An all-unchanged upsert notifies no listener, but its counts still reach the flush observer.
      this.flushIfIdle();
    }
    return stored;
  }

  /**
   * Applies `updater` to each keyed row in one sync transaction; missing keys, duplicate keys, and
   * updaters that return the existing row, or one equal to it field for field, are skipped. Prefer this over per-row `update` loops:
   * every commit fans out a delta batch to each attached live query, so N single-row commits cost
   * N pipeline runs where this costs one. Writes only apply at commit (after iteration finishes),
   * so passing live iterables over the collection being updated — row-map keys, index key sets —
   * is safe; nothing mutates mid-iteration.
   */
  public updateMany<K extends keyof Schema>(
    name: K,
    keys: Iterable<string>,
    updater: (row: Schema[K]) => Schema[K],
  ): void {
    const { collection, handles } = this.entry(name);
    // Sync writes don't land in _state until commit, so a duplicate key would re-read the
    // pre-transaction row and derive a second update from the stale base. Skip repeats instead.
    const seen = new Set<string>();
    // Only the keys actually written are dirty; skipped keys (absent, or an updater that returned
    // the existing row) must not cost a write on the next flush.
    const written: string[] = [];
    let began = false;
    try {
      for (const key of keys) {
        if (seen.has(key)) continue;
        seen.add(key);
        const existing = collection._state.get(key);
        if (existing === undefined) continue;
        const next = updater(existing);
        if (next === existing || sameRow(existing, next)) continue;
        if (!began) {
          handles.begin();
          began = true;
        }
        handles.write({ type: "update", value: next });
        written.push(key);
      }
    } finally {
      // Same rationale as upsert: a throwing updater must not wedge the sync transaction.
      if (began) {
        handles.commit();
        this.markUpserts(name, written);
        this.flushIfIdle();
      }
    }
  }

  /** Updates a single row in place if present; no-op (returns undefined) when the key is absent. */
  public update<K extends keyof Schema>(
    name: K,
    key: string,
    updater: (row: Schema[K]) => Schema[K],
  ): Schema[K] | undefined {
    const { collection, handles } = this.entry(name);
    const existing = collection._state.get(key);
    if (existing === undefined) return undefined;
    const next = updater(existing);
    if (next === existing || sameRow(existing, next)) return existing;
    handles.begin();
    handles.write({ type: "update", value: next });
    handles.commit();
    this.markUpserts(name, [key]);
    this.flushIfIdle();
    return next;
  }

  public delete<K extends keyof Schema>(name: K, keys: readonly string[]): void {
    if (keys.length === 0) return;
    const { collection, handles } = this.entry(name);
    const removed: string[] = [];
    let began = false;
    for (const key of keys) {
      if (!collection._state.has(key)) continue;
      if (!began) {
        handles.begin();
        began = true;
      }
      handles.write({ type: "delete", key });
      removed.push(key);
    }
    if (!began) return;
    handles.commit();
    this.markDeletes(name, removed);
    this.flushIfIdle();
  }

  /** Removes every row matching the predicate; returns the removed rows. */
  public deleteWhere<K extends keyof Schema>(name: K, predicate: (row: Schema[K]) => boolean): Schema[K][] {
    const removed: Schema[K][] = [];
    const keys: string[] = [];
    for (const [key, row] of this.getAll(name)) {
      if (predicate(row)) {
        removed.push(row);
        keys.push(key);
      }
    }
    this.delete(name, keys);
    return removed;
  }

  public clear(name: keyof Schema): void {
    const { collection, handles } = this.entry(name);
    if (collection.size === 0) return;
    handles.begin();
    handles.truncate();
    handles.commit();
    this.markTruncated(name);
    this.flushIfIdle();
  }

  /** Empties every collection except those listed (mirrors the old purge that spared fleet groups). */
  public reset(except: readonly (keyof Schema)[] = []): void {
    const spared = new Set(except);
    this.batch(() => {
      for (const name of this.entries.keys()) {
        if (!spared.has(name)) this.clear(name);
      }
    });
  }

  /**
   * Bulk-loads snapshot contents; replaces each provided collection wholesale. Restored rows came
   * off disk, so this deliberately records no dirty rows — marking them would make the first flush
   * after boot rewrite the entire workspace, which is the write amplification this tracking exists
   * to remove. Version counters still advance, so compute subscriptions see the load.
   */
  public load(data: { [K in keyof Schema]?: readonly Schema[K][] }): void {
    this.trackDirty = false;
    try {
      this.batch(() => {
        for (const name of Object.keys(data) as (keyof Schema)[]) {
          const rows = data[name];
          if (!rows) continue;
          this.clear(name);
          this.upsert(name, rows);
        }
      });
    } finally {
      this.trackDirty = true;
    }
  }

  /**
   * Coalesces write notifications: the write listeners (the snapshot scheduler) fire once when
   * the outermost batch completes. Supports async callbacks so an entire AppSync drain or
   * bootstrap page apply settles into a single notification sweep. Reads inside a batch see the
   * writes immediately — every write op commits its own sync transaction.
   *
   * Only writes belong inside: an open batch suppresses notifications app-wide, so awaiting I/O
   * in the callback stalls every live update until it returns. `warnOnLongOpenBatches` catches
   * that in development.
   */
  public batch<T>(fn: () => T): T {
    this.openBatch();
    let result: T;
    try {
      result = fn();
    } catch (error) {
      this.closeBatch();
      throw error;
    }
    if (result instanceof Promise) {
      return result.finally(() => this.closeBatch()) as T;
    }
    this.closeBatch();
    return result;
  }

  /**
   * Arms the long-open-batch watchdog: while a batch is open every write notification in the app
   * is suppressed, so an async batch that awaits I/O silently freezes live updates. Debug-mode
   * only — the app wires it from `config.debugMode`, and the warning carries the opening call
   * site's stack so the offending batch is identifiable in development.
   *
   * `batchDepth` is one counter shared by every batch, so independent top-level batches that
   * overlap in time nest into each other: the timer runs from the first opener until the last
   * overlapping batch closes, and reports that first opener's stack — which is not necessarily
   * the batch still holding notifications back.
   */
  public warnOnLongOpenBatches(thresholdMs: number): void {
    this.longOpenBatchWarningMs = thresholdMs;
  }

  /** Fires once per flush; drives the snapshot scheduler. */
  public subscribeWrites(listener: Listener): () => void {
    this.writeListeners.add(listener);
    return () => this.writeListeners.delete(listener);
  }

  /**
   * Tells `listener` which row keys each write op changed, as it commits: before the flush, so a compute reading the
   * change log on its next run sees every write that run follows. For computes that recompute only what a write
   * touched; everything else rides {@link subscribeWrites}.
   */
  public subscribeRowChanges(listener: (change: RowChange<keyof Schema>) => void): () => void {
    this.rowChangeListeners.add(listener);
    return () => this.rowChangeListeners.delete(listener);
  }

  /**
   * Hands each flush's per-collection row counts to `observer`, or stops with `null`. One observer at a time: the
   * flight recorder. Counting is skipped entirely while none is attached.
   */
  public observeFlushes(observer: FlushObserver | null): void {
    this.flushObserver = observer;
    this.flushCounts = new Map();
  }

  /**
   * Rows written since the last call, per collection; the snapshot layer persists only these. The
   * returned sets are handed over, not shared — later writes accumulate into a fresh map, so a
   * flush in flight can't have its work list mutated underneath it.
   */
  public consumeDirtyRows(): DirtyRows<Schema> {
    const dirty = this.dirtySinceSnapshot;
    this.dirtySinceSnapshot = new Map();
    return dirty;
  }

  /**
   * Merges a consumed dirty set back in — a flush that took the rows but couldn't write them (the
   * snapshot lock was held elsewhere) must not lose them. Writes that landed since the consume are
   * newer and win: a key already marked deleted is not resurrected as an upsert, and vice versa.
   */
  public restoreDirtyRows(dirty: DirtyRows<Schema>): void {
    for (const [name, incoming] of dirty) {
      const current = this.dirtyFor(name);
      current.truncated = current.truncated || incoming.truncated;
      for (const key of incoming.upserts) if (!current.deletes.has(key)) current.upserts.add(key);
      for (const key of incoming.deletes) if (!current.upserts.has(key)) current.deletes.add(key);
    }
  }

  public stats(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [name, entry] of this.entries) out[String(name)] = entry.collection.size;
    return out;
  }

  private entry<K extends keyof Schema>(name: K): StoreCollection<Schema[K]> {
    const entry = this.entries.get(name);
    if (!entry) throw new Error(`Unknown store collection: ${String(name)}`);
    return entry as StoreCollection<Schema[K]>;
  }

  private dirtyFor(name: keyof Schema): CollectionDirty {
    let entry = this.dirtySinceSnapshot.get(name);
    if (!entry) {
      entry = { truncated: false, upserts: new Set(), deletes: new Set() };
      this.dirtySinceSnapshot.set(name, entry);
    }
    return entry;
  }

  private markUpserts(name: keyof Schema, keys: Iterable<string>): void {
    this.touch(name);
    const list = [...keys];
    this.notifyRowChange({ collection: name, upserted: list, deleted: [], truncated: false });
    if (this.trackDirty) {
      const dirty = this.dirtyFor(name);
      for (const key of list) {
        dirty.deletes.delete(key);
        dirty.upserts.add(key);
      }
    }
    this.countFlush(name, "written", list.length);
  }

  private markDeletes(name: keyof Schema, keys: Iterable<string>): void {
    this.touch(name);
    const list = [...keys];
    this.notifyRowChange({ collection: name, upserted: [], deleted: list, truncated: false });
    if (this.trackDirty) {
      const dirty = this.dirtyFor(name);
      for (const key of list) {
        dirty.upserts.delete(key);
        dirty.deletes.add(key);
      }
    }
    this.countFlush(name, "deleted", list.length);
  }

  private notifyRowChange(change: RowChange<keyof Schema>): void {
    for (const listener of this.rowChangeListeners) listener(change);
  }

  private countFlush(name: keyof Schema, field: keyof FlushCounts, count: number): void {
    if (!this.flushObserver || count === 0) return;
    const key = String(name);
    const counts = this.flushCounts.get(key) ?? { written: 0, unchanged: 0, deleted: 0 };
    counts[field] += count;
    this.flushCounts.set(key, counts);
  }

  private markTruncated(name: keyof Schema): void {
    this.touch(name);
    this.notifyRowChange({ collection: name, upserted: [], deleted: [], truncated: true });
    if (!this.trackDirty) return;
    const dirty = this.dirtyFor(name);
    dirty.truncated = true;
    // The truncate subsumes them: the flush clears the store, so nothing recorded before it needs
    // a per-row write or delete.
    dirty.upserts.clear();
    dirty.deletes.clear();
  }

  private touch(name: keyof Schema): void {
    this.versions.set(name, (this.versions.get(name) ?? 0) + 1);
    this.pendingWrites = true;
  }

  private flushIfIdle(): void {
    if (this.batchDepth === 0) this.flush();
  }

  private openBatch(): void {
    this.batchDepth++;
    if (this.batchDepth !== 1 || this.longOpenBatchWarningMs === null) return;
    const thresholdMs = this.longOpenBatchWarningMs;
    const openedAt = new Error("Batch opened here").stack;
    this.longOpenBatchTimer = setTimeout(() => {
      this.longOpenBatchTimer = null;
      log.warn("Store batch held open past the warning threshold, suppressing write notifications", {
        thresholdMs,
        openedAt,
      });
    }, thresholdMs);
  }

  private closeBatch(): void {
    this.batchDepth--;
    if (this.batchDepth > 0) return;
    if (this.longOpenBatchTimer !== null) {
      clearTimeout(this.longOpenBatchTimer);
      this.longOpenBatchTimer = null;
    }
    this.flush();
  }

  private flush(): void {
    // The observer hears first, so a recorder files the writes before the computes they set off.
    if (this.flushObserver && this.flushCounts.size > 0) {
      const counts = this.flushCounts;
      this.flushCounts = new Map();
      this.flushObserver(counts);
    }
    if (!this.pendingWrites) return;
    this.pendingWrites = false;
    for (const listener of [...this.writeListeners]) listener();
  }
}
